import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { CalendarIcon, DollarSign, Building, ChevronDown, ChevronUp, Send } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { MarketplaceDeal } from '@/hooks/useMarketplaceDeals';
import { useCounterOffers } from '@/hooks/useCounterOffers';
import { useToast } from '@/hooks/use-toast';

interface DealApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  deal: MarketplaceDeal;
  onApply: (dealId: string) => Promise<{ success: boolean; applicationId?: string }>;
  defaultProposeDifferent?: boolean;
}

export const DealApplicationModal: React.FC<DealApplicationModalProps> = ({
  isOpen,
  onClose,
  deal,
  onApply,
  defaultProposeDifferent = false,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [proposeDifferent, setProposeDifferent] = useState(defaultProposeDifferent);

  // Sync with prop when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setProposeDifferent(defaultProposeDifferent);
    }
  }, [isOpen, defaultProposeDifferent]);
  const [proposedValue, setProposedValue] = useState(deal.deal_value);
  const [proposedDealType, setProposedDealType] = useState(deal.deal_type);
  const [proposedDeadline, setProposedDeadline] = useState<Date | undefined>(
    deal.deadline ? new Date(deal.deadline) : undefined
  );
  const [proposedTerms, setProposedTerms] = useState('');
  const [message, setMessage] = useState('');

  const { submitCounterOffer } = useCounterOffers();
  const { toast } = useToast();

  const resetForm = () => {
    setProposeDifferent(false);
    setProposedValue(deal.deal_value);
    setProposedDealType(deal.deal_type);
    setProposedDeadline(deal.deadline ? new Date(deal.deadline) : undefined);
    setProposedTerms('');
    setMessage('');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await onApply(deal.id);

      if (result.success && proposeDifferent && result.applicationId) {
        await submitCounterOffer({
          dealApplicationId: result.applicationId,
          dealId: deal.id,
          proposedValue,
          proposedDealType,
          proposedDeadline: proposedDeadline ? format(proposedDeadline, 'yyyy-MM-dd') : undefined,
          proposedTerms: proposedTerms || undefined,
          message: message || undefined,
        });
        toast({
          title: 'Application Submitted with Proposed Terms',
          description: 'Your application and counter-proposal have been sent to the brand.',
        });
      } else if (result.success) {
        toast({
          title: 'Application Submitted!',
          description: 'Your application has been submitted and is pending brand review.',
        });
      }

      resetForm();
      onClose();
    } catch (error: any) {
      console.error('Apply error:', error);
      toast({
        title: 'Failed to Submit Application',
        description: error?.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Apply for Deal</DialogTitle>
          <DialogDescription>
            Review the deal details and submit your application.
          </DialogDescription>
        </DialogHeader>

        {/* Deal Summary */}
        <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
          <h3 className="font-semibold text-base">{deal.title}</h3>
          {deal.brand_name && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Building className="h-4 w-4" />
              <span>{deal.brand_name}</span>
            </div>
          )}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1 font-medium">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              ${deal.deal_value.toLocaleString()}
            </div>
            {deal.deadline && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <CalendarIcon className="h-4 w-4" />
                {format(new Date(deal.deadline), 'MMM dd, yyyy')}
              </div>
            )}
          </div>
          {deal.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">{deal.description}</p>
          )}
        </div>

        {/* Optional Counter-Offer Section */}
        <Collapsible open={proposeDifferent} onOpenChange={setProposeDifferent}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between px-2 text-sm font-medium">
              Propose Different Terms
              {proposeDifferent ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="app-proposed-value">Proposed Value ($)</Label>
                <Input
                  id="app-proposed-value"
                  type="number"
                  value={proposedValue}
                  onChange={(e) => setProposedValue(Number(e.target.value))}
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <Label>Deal Type</Label>
                <Select value={proposedDealType} onValueChange={setProposedDealType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sponsorship">Sponsorship</SelectItem>
                    <SelectItem value="endorsement">Endorsement</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="appearance">Appearance</SelectItem>
                    <SelectItem value="ambassador">Ambassador</SelectItem>
                    <SelectItem value="collaboration">Collaboration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Proposed Deadline</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !proposedDeadline && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {proposedDeadline ? format(proposedDeadline, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={proposedDeadline}
                    onSelect={setProposedDeadline}
                    disabled={(date) => date < new Date()}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="app-terms">Additional Terms</Label>
              <Textarea
                id="app-terms"
                placeholder="Describe any specific terms or conditions..."
                value={proposedTerms}
                onChange={(e) => setProposedTerms(e.target.value)}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="app-message">Message to Brand</Label>
              <Textarea
                id="app-message"
                placeholder="Introduce yourself or explain your proposal..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || (proposeDifferent && proposedValue <= 0)}
            className="flex items-center gap-2"
          >
            <Send className="h-4 w-4" />
            {submitting ? 'Submitting...' : proposeDifferent ? 'Apply with Proposed Terms' : 'Submit Application'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
